import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TrendingUp, ChevronLeft, ChevronRight, Users, AlertTriangle } from 'lucide-react';

function fmtMonth(d) { return d.toISOString().slice(0, 7); }

const scoreColor = (s) =>
  s < 1 ? 'text-emerald-600' :
  s < 2 ? 'text-amber-600' :
  'text-red-600';

const scoreBg = (s) =>
  s < 1 ? 'bg-emerald-500/10 border-emerald-500/30' :
  s < 2 ? 'bg-amber-500/10 border-amber-500/30' :
  'bg-red-500/10 border-red-500/30';

const scoreLabel = (s) =>
  s < 1 ? 'Excellent' :
  s < 2 ? 'Watch' :
  'Needs Coaching';

export default function DepartmentPerformancePage() {
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [data, setData] = useState({ summary: {}, by_department: [], by_team: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/hr/department-performance?month=${month}`);
      setData(res.data || { summary: {}, by_department: [], by_team: [] });
    } catch {
      setData({ summary: {}, by_department: [], by_team: [] });
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const monthDate = new Date(month + '-01');
  const prevMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonth(fmtMonth(d)); };
  const nextMonth = () => { const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonth(fmtMonth(d)); };
  const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="dept-performance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-violet-500" /> Department &amp; Team Performance
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Discipline scorecard — late comings, half days, sick &amp; annual leaves grouped by department and team.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Active Employees</p>
          <p className="text-2xl font-bold">{s.total_employees || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Late Count</p>
          <p className="text-2xl font-bold text-amber-600">{s.total_late_count || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Half Days</p>
          <p className="text-2xl font-bold text-orange-600">{s.total_half_days || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Sick Leave Days</p>
          <p className="text-2xl font-bold text-blue-600">{s.total_sick_leave_days || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Annual Leave Days</p>
          <p className="text-2xl font-bold text-violet-600">{s.total_annual_leave_days || 0}</p>
        </CardContent></Card>
      </div>

      <Card className="border-violet-500/30 bg-violet-500/5">
        <CardContent className="p-4 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-violet-700">Discipline Score</p>
            <p className="text-muted-foreground">
              Lower = better. Formula: (late + half×2 + absent×3 + sick + annual×0.5) ÷ headcount.
              Helps HR identify managers who maintain professional conduct vs. teams that need coaching.
            </p>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="department">
        <TabsList>
          <TabsTrigger value="department" data-testid="dp-tab-dept">By Department</TabsTrigger>
          <TabsTrigger value="team" data-testid="dp-tab-team">By Team / Manager</TabsTrigger>
        </TabsList>

        <TabsContent value="department" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Department</th>
                      <th className="text-right px-2 py-2 font-medium">Headcount</th>
                      <th className="text-right px-2 py-2 font-medium">Late</th>
                      <th className="text-right px-2 py-2 font-medium">Half Day</th>
                      <th className="text-right px-2 py-2 font-medium">Absent</th>
                      <th className="text-right px-2 py-2 font-medium">Sick</th>
                      <th className="text-right px-2 py-2 font-medium">Annual</th>
                      <th className="text-right px-2 py-2 font-medium">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} className="p-10 text-center">
                        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                      </td></tr>
                    ) : (data.by_department || []).length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No data for {monthLabel}</td></tr>
                    ) : data.by_department.map((d, i) => (
                      <tr key={i} className="border-b hover:bg-muted/20" data-testid={`dp-dept-row-${d.department}`}>
                        <td className="px-3 py-2 font-medium">{d.department}</td>
                        <td className="px-2 py-2 text-right">{d.headcount}</td>
                        <td className="px-2 py-2 text-right text-amber-600">{d.late_count}</td>
                        <td className="px-2 py-2 text-right text-orange-600">{d.half_day_count}</td>
                        <td className="px-2 py-2 text-right text-red-600">{d.absent_count}</td>
                        <td className="px-2 py-2 text-right text-blue-600">{d.sick_leave_days}</td>
                        <td className="px-2 py-2 text-right text-violet-600">{d.annual_leave_days}</td>
                        <td className={`px-2 py-2 text-right font-bold ${scoreColor(d.discipline_score)}`}>
                          {d.discipline_score}
                          <Badge variant="outline" className={`ml-1 text-[9px] ${scoreBg(d.discipline_score)}`}>
                            {scoreLabel(d.discipline_score)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Team</th>
                      <th className="text-left px-2 py-2 font-medium">Manager(s)</th>
                      <th className="text-left px-2 py-2 font-medium">Department</th>
                      <th className="text-right px-2 py-2 font-medium">HC</th>
                      <th className="text-right px-2 py-2 font-medium">Late</th>
                      <th className="text-right px-2 py-2 font-medium">Half</th>
                      <th className="text-right px-2 py-2 font-medium">Absent</th>
                      <th className="text-right px-2 py-2 font-medium">Sick</th>
                      <th className="text-right px-2 py-2 font-medium">Annual</th>
                      <th className="text-right px-2 py-2 font-medium">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={10} className="p-10 text-center">
                        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                      </td></tr>
                    ) : (data.by_team || []).length === 0 ? (
                      <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No data for {monthLabel}</td></tr>
                    ) : data.by_team.map((t, i) => (
                      <tr key={i} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-2 font-medium">{t.team_id}</td>
                        <td className="px-2 py-2 text-xs">
                          {(t.managers || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {t.managers.map((m, j) => (
                                <Badge key={j} variant="secondary" className="text-[10px]">
                                  <Users className="h-3 w-3 mr-1" /> {m}
                                </Badge>
                              ))}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">{t.department}</td>
                        <td className="px-2 py-2 text-right">{t.headcount}</td>
                        <td className="px-2 py-2 text-right text-amber-600">{t.late_count}</td>
                        <td className="px-2 py-2 text-right text-orange-600">{t.half_day_count}</td>
                        <td className="px-2 py-2 text-right text-red-600">{t.absent_count}</td>
                        <td className="px-2 py-2 text-right text-blue-600">{t.sick_leave_days}</td>
                        <td className="px-2 py-2 text-right text-violet-600">{t.annual_leave_days}</td>
                        <td className={`px-2 py-2 text-right font-bold ${scoreColor(t.discipline_score)}`}>
                          {t.discipline_score}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
